import axios from 'axios';

import { useAuthStore } from '@/stores/authStore';
import { env } from '@/lib/env';
import type { AuthSession } from '@/types/api';

/**
 * Refresh-token exchange, kept out of the API client on purpose.
 *
 * The response interceptor of `apiClient` calls this function, so it must not be routed through the same client
 * (that would recurse). It therefore uses bare `axios`, relies entirely on the `HttpOnly` `pf_rt` cookie the API
 * writes (ADR-0005), and never sees or stores the refresh token itself.
 *
 * Only one refresh is ever in flight: concurrent 401s share the same promise, which is what keeps a burst of
 * parallel requests from burning the rotation chain (a second exchange with the old token would be treated as
 * token reuse and revoke the whole family).
 */

let inFlightRefresh: Promise<AuthSession | null> | null = null;

/** Performs the exchange exactly once and resolves to the new session, or `null` when it failed. */
async function performRefresh(): Promise<AuthSession | null> {
  try {
    const response = await axios.post<AuthSession>(
      `${env.apiBaseUrl}/auth/refresh`,
      {},
      {
        withCredentials: true,
        timeout: env.apiTimeoutMs,
        headers: { Accept: 'application/json' },
      },
    );

    useAuthStore.getState().setSession(response.data);
    return response.data;
  } catch {
    // A rejected refresh means the device has no usable session any more; the caller clears the store.
    return null;
  }
}

/**
 * Reveals the refresh-token cookie to the browser so the SPA can restore a session after a reload.
 *
 * @returns The new session, or `null` when the device has no valid refresh token.
 */
export function refreshAccessToken(): Promise<AuthSession | null> {
  inFlightRefresh ??= performRefresh().finally(() => {
    inFlightRefresh = null;
  });

  return inFlightRefresh;
}

/** Test seam: drops a pending refresh promise so one test cannot leak into the next. */
export function resetRefreshState(): void {
  inFlightRefresh = null;
}
